import { useTranslation } from 'react-i18next';

import { getVersionLabel } from '@/components/atoms/VersionTooltipContent/utils';
import { SkeletonSpan } from '@/components/ui/skeleton';
import useBrains from '@/hooks/useBrains';
import { Action, ActionType } from '@/shared/types/rules';

import { useBundleOptions } from './useBundleOptions';
import { useCollectionOptions } from './useCollectionOptions';
import { useDepartmentOptions } from './useDepartmentOptions';

type ValueProps = { id: string; fallback?: string };
type ActionLabelProps = { action: Action; fallback?: string };
type OptionsState = { options: { value: string; label: string }[]; isLoading: boolean };

const ValueSkeleton = () => <SkeletonSpan className="inline-block h-3 w-24 align-middle" />;

const renderOption = ({ options, isLoading }: OptionsState, id: string, fallback?: string) => {
  const label = options.find(({ value }) => value === id)?.label;
  if (fallback === undefined) {
    return label ?? '';
  }
  if (!label && isLoading) {
    return <ValueSkeleton />;
  }
  return label || fallback;
};

const BrainValue = ({ action, fallback }: ActionLabelProps) => {
  const { t } = useTranslation();
  const { brain, isBrainLoading } = useBrains(action.brain_parent_id);
  if (fallback !== undefined && !brain?.name) {
    return isBrainLoading ? <ValueSkeleton /> : <>{fallback}</>;
  }
  return <>{`${brain?.name ?? ''} · ${getVersionLabel(t, action.brain_version)}`}</>;
};

const DepartmentValue = ({ id, fallback }: ValueProps) => (
  <>{renderOption(useDepartmentOptions(), id, fallback)}</>
);

const BundleValue = ({ id, fallback }: ValueProps) => (
  <>{renderOption(useBundleOptions(), id, fallback)}</>
);

const CollectionValue = ({ id, fallback }: ValueProps) => (
  <>{renderOption(useCollectionOptions(), id, fallback)}</>
);

/**
 * Shows the action's selected value. With a `fallback`, a skeleton shows while the value loads
 * and the fallback shows when the value no longer exists.
 */
export const ActionValueLabel = ({ action, fallback }: ActionLabelProps) => {
  switch (action.type) {
    case ActionType.ASSIGN_BRAIN:
      return action.brain_parent_id ? <BrainValue action={action} fallback={fallback} /> : null;
    case ActionType.ASSIGN_DEPARTMENT:
      return action.department_id ? (
        <DepartmentValue id={action.department_id} fallback={fallback} />
      ) : null;
    case ActionType.APPLY_BUNDLE:
      return action.bundle_id ? <BundleValue id={action.bundle_id} fallback={fallback} /> : null;
    case ActionType.ASSIGN_COLLECTION:
      return action.collection_id ? (
        <CollectionValue id={action.collection_id} fallback={fallback} />
      ) : null;
    case ActionType.ADD_TAG:
      return action.tag ? <>{action.tag}</> : null;
    default:
      return null;
  }
};
